"use client";

import { useEffect, useMemo, useState } from "react";
import { FiPlus } from "react-icons/fi";
import { useGetFAQQuery } from "@/store/api/faqApi";
import type { FAQItem, FAQModule } from "@/types/api";
import { handleApiError } from "@/utils/api";

type FAQListProps = {
  type?: string;
  searchTerm?: string;
};

const FAQList = ({
  type = "",
  searchTerm = "",
}: FAQListProps) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  // Fetch FAQ data from API (without type parameter)
  const { 
    data: faqResponse, 
    isLoading, 
    error, 
    refetch 
  } = useGetFAQQuery({
    search: searchTerm || undefined,
  });

  const toggle = (i: number) => {
    setOpenIndex(openIndex === i ? null : i);
  };

  // Transform modules into flat FAQ items array
  const faqData: FAQItem[] = useMemo(() => {
    if (faqResponse?.data) {
      const modules: FAQModule[] = faqResponse.data;
      const items: FAQItem[] = [];
      
      modules.forEach((module) => {
        module.questions.forEach((question) => {
          items.push({
            id: `${module.moduleId}-${question.priority}`,
            q: question.question,
            a: question.answer,
            type: module.moduleName.toLowerCase(),
            category: module.moduleName,
            moduleId: module.moduleId,
            priority: question.priority,
          });
        });
      });
      
      return items;
    }
    return [];
  }, [faqResponse]);

  // Client-side filtering by type and search term
  const filteredData = useMemo(() => {
    let filtered = faqData;

    // Filter by type (module name) if provided
    if (type) {
      filtered = filtered.filter((item) => {
        // Check if item's type (module name) matches
        const itemType = item.type || item.category || '';
        return itemType.toLowerCase() === type.toLowerCase();
      });
    }

    // Filter by search term if provided
    const query = searchTerm.trim().toLowerCase();
    if (query) {
      filtered = filtered.filter(
        (item) =>
          item.q.toLowerCase().includes(query) ||
          item.a.toLowerCase().includes(query)
      );
    }

    // Sort by priority if available
    filtered.sort((a, b) => {
      const priorityA = (a as any).priority ?? 999;
      const priorityB = (b as any).priority ?? 999;
      return priorityA - priorityB;
    });

    return filtered;
  }, [faqData, type, searchTerm]);

  useEffect(() => {
    setOpenIndex(null);
  }, [searchTerm, type]);

  // Loading state
  if (isLoading) {
    return (
      <div className="mt-7.5 text-center text-text7 max-lg:text-[#262626] dark:text-white">
        <div className="flex items-center justify-center gap-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <span>Loading FAQs...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="mt-7.5 text-center text-text7 max-lg:text-[#262626] dark:text-white">
        <p className="text-red-500 mb-2">Error loading FAQs</p>
        <p className="text-sm text-gray-500">{handleApiError(error)}</p>
        <button
          onClick={() => refetch()}
          className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/80 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // No data state
  if (!filteredData.length) {
    return (
      <div className="mt-15 text-center text-white space-y-10">
        <p className="text-base leading-7 font-inter">
          {searchTerm.trim() ? (
            <>
              No results found for:{" "}
              <span className="font-bold">"{searchTerm.trim()}"</span>
            </>
          ) : type ? (
            "No questions available for this category"
          ) : (
            "No FAQs available"
          )}
        </p>
        <p className="text-base leading-7 font-inter">
          No FAQs found matching your criteria
        </p>
      </div>
    );
  }

  return (
    <div className="grid lg:grid-cols-2 gap-5 mt-7.5">
      {filteredData?.map((item, index) => {
        const open = openIndex === index;
        // Use item.id if available, otherwise fallback to index
        const itemKey = item.id || index;

        return (
          <div
            key={itemKey}
            className={`p-5 bg-[#da7927] rounded-2xl transition-all duration-300 ${
              open ? "h-full" : "lg:h-16"
            }`}
          >
            <div
              className="flex items-center justify-between gap-4 text-white cursor-pointer"
              onClick={() => toggle(index)}
            >
              <h3 className="font-bold text-xs lg:text-base">{item.q}</h3>

              <FiPlus
                className={`text-xl shrink-0 transition-transform duration-300 ${
                  open ? "rotate-45" : ""
                }`}
              />
            </div>

            <div
              className={`transition-all duration-300 overflow-hidden ${
                open ? "mt-5 max-h-60 opacity-100" : "max-h-0 opacity-0 mt-0"
              }`}
            >
              <div 
                className="text-xs text-text1 lg:text-sm prose prose-sm max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: item.a }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FAQList;
